
import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { AnimatedCard } from '@/components/ui/AnimatedCard';
import MaintenanceTabs from '@/components/dashboard/maintenance/MaintenanceTabs';
import { useToast } from '@/hooks/use-toast';
import { Device } from './gis-map/types';
import MaintenanceHeader from './maintenance/MaintenanceHeader';
import MaintenanceHistoryTimeline from './maintenance/MaintenanceHistoryTimeline';
import { useMaintenanceHistory } from './maintenance/hooks/useMaintenanceHistory';

interface DeviceMaintenanceTabProps {
  deviceType?: 'energy' | 'gas' | 'water';
  smartMeters: any[];
  gateways: any[];
  selectedDevice: { id: string; type: string } | null;
  deviceData?: Device | null;
}

const DeviceMaintenanceTab: React.FC<DeviceMaintenanceTabProps> = ({ 
  deviceType = 'energy',
  smartMeters,
  gateways,
  selectedDevice,
  deviceData
}) => {
  const { toast } = useToast();
  const { maintenanceHistory, getNextMaintenance } = useMaintenanceHistory(selectedDevice);

  useEffect(() => {
    if (deviceData && selectedDevice) {
      toast({
        title: "Maintenance Data Loaded",
        description: `Viewing maintenance for ${deviceData.name} (${selectedDevice.id})`,
      });
    }
  }, [deviceData, selectedDevice, toast]);

  const getTitle = () => {
    if (deviceData && selectedDevice) {
      return `Maintenance Dashboard - ${deviceData.name} (${selectedDevice.id})`;
    }
    return `${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)} Maintenance Dashboard`;
  };

  return (
    <div className="space-y-6">
      <AnimatedCard
        animation="fade-up"
        delay={100}
        className="border-none shadow-sm"
      >
        <Card>
          <CardHeader className="pb-2">
            <MaintenanceHeader 
              title={getTitle()} 
              nextMaintenance={deviceData && selectedDevice ? getNextMaintenance() : undefined}
            />
          </CardHeader>
          <CardContent>
            <MaintenanceTabs 
              defaultTab="alerts" 
              selectedDeviceId={selectedDevice?.id}
              deviceData={deviceData}
            />
          </CardContent>
        </Card>
      </AnimatedCard>

      {deviceData && selectedDevice && maintenanceHistory.length > 0 && (
        <AnimatedCard
          animation="fade-up"
          delay={200}
          className="border-none shadow-sm"
        >
          <Card>
            <CardHeader className="pb-2">
              <MaintenanceHeader title="Maintenance History" />
            </CardHeader>
            <CardContent>
              <MaintenanceHistoryTimeline maintenanceHistory={maintenanceHistory} />
            </CardContent>
          </Card>
        </AnimatedCard>
      )}
    </div>
  );
};

export default DeviceMaintenanceTab;
